import { Injectable, InjectionToken, Provider } from '@angular/core';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import {
	DatabaseViewAssignmentParamRequest,
	DatabaseViewInstitutionParamRequest,
	DatabaseViewLekParamRequest,
} from '../api-types/database-view-request.api';
import {
	ApiDatabaseViewAssignmentResponse,
	ApiDatabaseViewInstitutionResponse,
	ApiDatabaseViewLekResponse,
	ApiDatabaseViewPage,
	ApiDatabaseViewSchema,
} from '../api-types/database-view.api';
import {
	DatabaseViewAssignmentResponse,
	DatabaseViewInstitutionResponse,
	DatabaseViewLekResponse,
	DatabaseViewSchema,
	DatabaseViewSponsorResponse,
} from '../app-types/database-view.app';
import {
	transformApiDatabaseViewAssignmentResponsesToApp,
	transformApiDatabaseViewInstitutionResponsesToApp,
	transformApiDatabaseViewLekResponsesToApp,
	transformApiDatabaseViewSchemaToApp,
} from '../transformers/database-view.transform';
import { BaseRequestService } from './base-request.service';

export const DATABASE_VIEW_API = new InjectionToken<DatabaseViewApi>('DATABASE_VIEW_API');

export interface DatabaseViewApiProvider {
	getAssignmentsSchema(): Promise<DatabaseViewSchema>;
	getInstitutionsSchema(): Promise<DatabaseViewSchema>;
	getLeksSchema(): Promise<DatabaseViewSchema>;
	getAssignments(
		body: DatabaseViewAssignmentParamRequest,
		offset?: number,
		range?: number,
		signal?: AbortSignal,
	): Promise<PaginatedResponse<DatabaseViewAssignmentResponse>>;
	getInstitutions(
		body: DatabaseViewInstitutionParamRequest,
		offset?: number,
		range?: number,
		signal?: AbortSignal,
	): Promise<PaginatedResponse<DatabaseViewInstitutionResponse>>;
	getLeks(
		body: DatabaseViewLekParamRequest,
		offset?: number,
		range?: number,
		signal?: AbortSignal,
	): Promise<PaginatedResponse<DatabaseViewLekResponse>>;
	getAssignmentsCsv(body: DatabaseViewAssignmentParamRequest): Promise<Blob>;
	getInstitutionsCsv(body: DatabaseViewInstitutionParamRequest): Promise<Blob>;
	getLeksCsv(body: DatabaseViewLekParamRequest): Promise<Blob>;
	getInstitutionCategories(
		offset?: number,
		range?: number,
		searchTerm?: string,
		showHiddenEntries?: boolean,
	): Promise<PaginatedResponse<string>>;
	filterSponsors(
		offset?: number,
		range?: number,
		searchTerm?: string,
		showHiddenEntries?: boolean,
	): Promise<PaginatedResponse<DatabaseViewSponsorResponse>>;
}

@Injectable()
export class DatabaseViewApi extends BaseRequestService implements DatabaseViewApiProvider {
	private getSchema = async (path: string, expectedView: string): Promise<DatabaseViewSchema> => {
		const response = await this.get<ApiDatabaseViewSchema>(path);
		if (response.view !== expectedView || response.version !== 1 || !Array.isArray(response.columns)) {
			throw new Error('Unsupported database view schema');
		}
		return transformApiDatabaseViewSchemaToApp(response);
	};

	private validateRequest(body: { selectedColumns: string[] }): void {
		if (!body.selectedColumns.length || new Set(body.selectedColumns).size !== body.selectedColumns.length) {
			throw new Error('Invalid database view column selection');
		}
	}

	private validatePage(response: ApiDatabaseViewPage, body: { selectedColumns: string[] }): void {
		this.validateRequest(body);
		if (!response || !Array.isArray(response.columns) || !Array.isArray(response.rows)) {
			throw new Error('Invalid database view page');
		}
		const responseColumns = new Set(response.columns.map((column) => column.id));
		if (body.selectedColumns.some((column) => !responseColumns.has(column))) {
			throw new Error('Database view response is missing a selected column');
		}
	}

	public getAssignmentsSchema = () => this.getSchema('databaseView/assignments/schema', 'assignments');
	public getInstitutionsSchema = () => this.getSchema('databaseView/institutions/schema', 'institutions');
	public getLeksSchema = () => this.getSchema('databaseView/assistance-results/schema', 'assistance-results');

	public getAssignments = async (
		body: DatabaseViewAssignmentParamRequest,
		offset?: number,
		range?: number,
		signal?: AbortSignal,
	): Promise<PaginatedResponse<DatabaseViewAssignmentResponse>> => {
		const response = await this.post<ApiDatabaseViewPage, DatabaseViewAssignmentParamRequest>(
			'databaseView/assignments',
			body,
			{ offset, range },
			{ signal },
		);
		this.validatePage(response, body);
		return {
			entities: transformApiDatabaseViewAssignmentResponsesToApp(
				response.rows as unknown as ApiDatabaseViewAssignmentResponse[],
			),
			totalEntities: response.totalEntities,
			offset: response.offset,
			range: response.range,
		};
	};

	public getInstitutions = async (
		body: DatabaseViewInstitutionParamRequest,
		offset?: number,
		range?: number,
		signal?: AbortSignal,
	): Promise<PaginatedResponse<DatabaseViewInstitutionResponse>> => {
		const response = await this.post<ApiDatabaseViewPage, DatabaseViewInstitutionParamRequest>(
			'databaseView/institutions',
			body,
			{ offset, range },
			{ signal },
		);
		this.validatePage(response, body);
		return {
			entities: transformApiDatabaseViewInstitutionResponsesToApp(
				response.rows as unknown as ApiDatabaseViewInstitutionResponse[],
			),
			totalEntities: response.totalEntities,
			offset: response.offset,
			range: response.range,
		};
	};

	public getInstitutionCategories = async (
		offset?: number,
		range?: number,
		searchTerm?: string,
		showHiddenEntries?: boolean,
	): Promise<PaginatedResponse<string>> => {
		return await this.get<PaginatedResponse<string>>('databaseView/institutions/categories', {
			offset,
			range,
			searchTerm,
			showHiddenEntries,
		});
	};

	public getLeks = async (
		body: DatabaseViewLekParamRequest,
		offset?: number,
		range?: number,
		signal?: AbortSignal,
	): Promise<PaginatedResponse<DatabaseViewLekResponse>> => {
		const response = await this.post<ApiDatabaseViewPage, DatabaseViewLekParamRequest>(
			'databaseView/assistance-results',
			body,
			{ offset, range },
			{ signal },
		);
		this.validatePage(response, body);
		return {
			entities: transformApiDatabaseViewLekResponsesToApp(response.rows as unknown as ApiDatabaseViewLekResponse[]),
			totalEntities: response.totalEntities,
			offset: response.offset,
			range: response.range,
		};
	};

	public getAssignmentsCsv = async (body: DatabaseViewAssignmentParamRequest): Promise<Blob> => {
		this.validateRequest(body);
		return this.post<Blob, DatabaseViewAssignmentParamRequest>(
			'databaseView/assignments/export/csv',
			body,
			{},
			{
				responseType: 'blob',
			},
		);
	};

	public getInstitutionsCsv = async (body: DatabaseViewInstitutionParamRequest): Promise<Blob> => {
		this.validateRequest(body);
		return this.post<Blob, DatabaseViewInstitutionParamRequest>(
			'databaseView/institutions/export/csv',
			body,
			{},
			{
				responseType: 'blob',
			},
		);
	};

	public getLeksCsv = async (body: DatabaseViewLekParamRequest): Promise<Blob> => {
		this.validateRequest(body);
		return this.post<Blob, DatabaseViewLekParamRequest>(
			'databaseView/assistance-results/export/csv',
			body,
			{},
			{
				responseType: 'blob',
			},
		);
	};

	public filterSponsors = async (
		offset?: number,
		range?: number,
		searchTerm?: string,
		showHiddenEntries?: boolean,
	): Promise<PaginatedResponse<DatabaseViewSponsorResponse>> => {
		const response = await this.get<PaginatedResponse<DatabaseViewSponsorResponse>>('databaseView/sponsors/filters', {
			offset,
			range,
			searchTerm,
			showHiddenEntries,
		});
		return {
			entities: response.entities,
			totalEntities: response.totalEntities,
			offset: response.offset,
			range: response.range,
		};
	};
}

export const provideDatabaseViewApi: Provider = {
	provide: DATABASE_VIEW_API,
	useClass: DatabaseViewApi,
};
